import type { Player, Round, Score, TeeSet } from "../types.js";
import {
  allocateStrokes,
  courseHandicap,
  effectiveAllowancePercent,
  HALF_ROUND,
  holesInPlay,
  holesPlayedCount,
  playingHandicap,
} from "../handicap.js";
import { splitPlacementPoints } from "../ties.js";
import type { RoundResult } from "./types.js";

export interface PlayerHandicapInfo {
  courseHandicap: number;
  playingHandicap: number;
  strokesByHole: Map<number, number>;
}

/** Computes each player's course/playing handicap and per-hole strokes received for a round. */
export function computeHandicaps(
  round: Round,
  course: TeeSet,
  players: Player[],
  allowancePercentOverride?: number,
): Map<string, PlayerHandicapInfo> {
  const holes = holesInPlay(course.holes, round.holeSet);
  // The allowance is halved for a nine, and `holes` is what decides whether this is
  // one — a nine-hole course plays its whole card and is still a nine.
  const allowance = effectiveAllowancePercent(
    allowancePercentOverride ?? round.handicapAllowance,
    holes.length,
  );
  const result = new Map<string, PlayerHandicapInfo>();
  for (const player of players) {
    const ch = courseHandicap(player.handicapIndex, course.slope);
    const ph = playingHandicap(ch, allowance);
    result.set(player.id, {
      courseHandicap: ch,
      playingHandicap: ph,
      strokesByHole: allocateStrokes(ph, holes),
    });
  }
  return result;
}

export function holeNumbersInPlay(round: Round, course: TeeSet): number[] {
  return holesInPlay(course.holes, round.holeSet).map((h) => h.number);
}

/** The holes a scorer should consider: the round's hole set, narrowed to `onlyHoles`
 * when the caller is asking for a partial (live) result. */
export function scoringHoles(round: Round, course: TeeSet, onlyHoles?: number[]): number[] {
  const inPlay = holeNumbersInPlay(round, course);
  if (!onlyHoles) return inPlay;
  const allowed = new Set(onlyHoles);
  return inPlay.filter((h) => allowed.has(h));
}

/**
 * How many holes this round plays — read off the card, not off `round.holeSet`.
 *
 * Every format's rules text says "across N holes", and N was 18 unless the round asked
 * for a nine. On a nine-hole course it is 9 whatever the round asked for, so the count
 * has to come from the tee set that is being played.
 */
export function holeCount(round: Round, course: TeeSet | undefined): number {
  return holesPlayedCount(course?.holes, round.holeSet);
}

/** The parenthetical every format's rules text carries when the allowance is halved. */
export function allowanceNote(round: Round, course: TeeSet | undefined): string {
  return holeCount(round, course) <= HALF_ROUND ? " (halved for a 9-hole round)" : "";
}

/** "Sam", "Sam and Jo", "Sam, Jo and Pat" — a list that reads like a sentence rather
 * than a join. Used by the contest standings, where ties are common enough to notice. */
export function listNames(names: string[]): string {
  if (names.length <= 2) return names.join(" and ");
  return `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}

export function parByHole(course: TeeSet, round: Round): Map<number, number> {
  const map = new Map<number, number>();
  for (const h of holesInPlay(course.holes, round.holeSet)) map.set(h.number, h.par);
  return map;
}

function scoreKey(playerId: string, hole: number): string {
  return `${playerId}:${hole}`;
}

export function buildScoreMap(scores: Score[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const s of scores) {
    if (s.strokes != null) map.set(scoreKey(s.playerId, s.hole), s.strokes);
  }
  return map;
}

export function grossOnHole(
  scoreMap: Map<string, number>,
  playerId: string,
  hole: number,
): number | null {
  return scoreMap.get(scoreKey(playerId, hole)) ?? null;
}

export function netOnHole(
  scoreMap: Map<string, number>,
  handicaps: Map<string, PlayerHandicapInfo>,
  playerId: string,
  hole: number,
): number | null {
  const gross = grossOnHole(scoreMap, playerId, hole);
  if (gross == null) return null;
  const received = handicaps.get(playerId)?.strokesByHole.get(hole) ?? 0;
  return gross - received;
}

/** For single-ball team formats (scramble, alternate shot): whichever team member's score was
 * recorded for that hole represents the team's shared ball. */
export function teamGrossOnHole(
  scoreMap: Map<string, number>,
  team: string[],
  hole: number,
): number | null {
  for (const playerId of team) {
    const g = grossOnHole(scoreMap, playerId, hole);
    if (g != null) return g;
  }
  return null;
}

/** For two-ball team formats (four-ball, shamble): the better (lower) net score among teammates. */
export function bestTeamNetOnHole(
  scoreMap: Map<string, number>,
  handicaps: Map<string, PlayerHandicapInfo>,
  team: string[],
  hole: number,
): number | null {
  let best: number | null = null;
  for (const playerId of team) {
    const net = netOnHole(scoreMap, handicaps, playerId, hole);
    if (net != null && (best == null || net < best)) best = net;
  }
  return best;
}

export function grossTotal(
  scoreMap: Map<string, number>,
  playerId: string,
  holes: number[],
): number | null {
  let total = 0;
  for (const h of holes) {
    const g = grossOnHole(scoreMap, playerId, h);
    if (g == null) return null;
    total += g;
  }
  return total;
}

export function netTotal(
  scoreMap: Map<string, number>,
  handicaps: Map<string, PlayerHandicapInfo>,
  playerId: string,
  holes: number[],
): number | null {
  let total = 0;
  for (const h of holes) {
    const net = netOnHole(scoreMap, handicaps, playerId, h);
    if (net == null) return null;
    total += net;
  }
  return total;
}

/**
 * Segment bounds are positions within the round's hole set, not absolute hole
 * numbers (spec §2: "inclusive, relative to the round's hole set"). For an 18-hole
 * or front-nine round the two coincide, but on a back nine segment 1-3 means holes
 * 10-12 — reading it as holes 1-3 would score a back-nine segment against holes
 * that were never played.
 */
export function holesInSegment(
  holeNumbersInPlay: number[],
  startHole: number,
  endHole: number,
  onlyHoles?: number[],
): number[] {
  const slice = holeNumbersInPlay.slice(Math.max(0, startHole - 1), endHole);
  if (!onlyHoles) return slice;
  const allowed = new Set(onlyHoles);
  return slice.filter((h) => allowed.has(h));
}

/**
 * The point table for a segment with `sideCount` sides: the best side takes `win` and
 * every other side takes `loss`, with ties splitting as everywhere else.
 *
 * For two sides this is exactly [win, loss] — identical to the old two-team behaviour,
 * tie split included. It generalises because a segment is a match between sides, and
 * "won it" / "didn't" is the only reading of a win/loss pair that survives a third side.
 */
export function segmentPointTable(
  teamPoints: { win: number; loss: number },
  sideCount: number,
): number[] {
  return [teamPoints.win, ...Array<number>(Math.max(0, sideCount - 1)).fill(teamPoints.loss)];
}

/**
 * The "what a segment pays" rules line, shared by every team format so they can't drift
 * apart. Wording follows the round's actual shape: two sides read as a head-to-head,
 * three or more as a race for the win points.
 */
export function describeSegmentPoints(round: Round, tieNote = ""): string {
  const tp = round.teamPoints ?? { win: 0, loss: 0 };
  const sides = Math.max(2, ...round.segments.map((s) => s.teams.filter((t) => t.length > 0).length));
  const tie = `a tie${tieNote} splits ${(tp.win + tp.loss) / 2} each`;
  return sides > 2
    ? `Each segment is its own match between ${sides} sides: the best side takes ${tp.win} points, the rest take ${tp.loss}, and ${tie}.`
    : `Each segment is its own match: win ${tp.win} points, loss ${tp.loss}, ${tie}.`;
}

/**
 * Scores every segment of a team round as an independent match (spec §2): each
 * segment sums a per-hole team value over its holes, lower total wins, teamPoints
 * (or their average on a tie) pay the winning/losing sides. Shared by four-ball,
 * shamble, scramble, and alternate shot — they differ only in how a team's value on
 * a given hole is computed.
 *
 * Any number of sides, not just two. Reading only teams[0] and teams[1] meant a six-
 * player round — where the rotation makes three pairs per segment — silently paid the
 * third pair nothing at all.
 */
export function scoreSegmentsByTotal(
  round: Round,
  players: Player[],
  holeNumbers: number[],
  teamValueOnHole: (team: string[], hole: number) => number | null,
  onlyHoles?: number[],
): RoundResult {
  const points: Record<string, number> = {};
  for (const p of players) points[p.id] = 0;
  const segmentResults: NonNullable<RoundResult["segments"]> = [];

  for (const segment of round.segments) {
    const allHoles = holesInSegment(holeNumbers, segment.startHole, segment.endHole);
    const holes = onlyHoles ? allHoles.filter((h) => onlyHoles.includes(h)) : allHoles;
    const sides = segment.teams.filter((t) => t.length > 0);
    if (sides.length < 2) continue;

    const totalFor = (team: string[]): number | null => {
      let total = 0;
      for (const h of holes) {
        const v = teamValueOnHole(team, h);
        if (v == null) return null;
        total += v;
      }
      return total;
    };

    const totals = holes.length > 0 ? sides.map(totalFor) : sides.map(() => null);
    const segPoints: Record<string, number> = {};

    // A segment scores only once every side has a complete total — a half-played
    // match has no result, and a side that hasn't posted yet isn't losing it.
    if (totals.every((v): v is number => v != null)) {
      const teamPoints = round.teamPoints ?? { win: 0, loss: 0 };
      const split = splitPlacementPoints(
        totals.map((value, i) => ({ id: String(i), value })),
        segmentPointTable(teamPoints, sides.length),
        { lowerIsBetter: true },
      );
      sides.forEach((team, i) => {
        for (const id of team) segPoints[id] = split.get(String(i)) ?? 0;
      });
      for (const [id, pts] of Object.entries(segPoints)) points[id] = (points[id] ?? 0) + pts;
    }

    segmentResults.push({
      startHole: segment.startHole,
      endHole: segment.endHole,
      points: segPoints,
      holesScored: holes.length,
      holesTotal: allHoles.length,
    });
  }

  return { roundId: round.id, points, segments: segmentResults, provisional: Boolean(onlyHoles) };
}

/**
 * Sides in a segment, paired into the matches they actually play: side 1 v side 2,
 * side 3 v side 4, and so on. Used by match play, which is the one format where a
 * segment holding four sides means two matches rather than a four-way race.
 *
 * A trailing unpaired side has nobody to play and sits the segment out —
 * `validateSegments` warns about it rather than the scorer inventing an opponent.
 */
export function pairSidesIntoMatches(sides: string[][]): [string[], string[]][] {
  const matches: [string[], string[]][] = [];
  for (let i = 0; i + 1 < sides.length; i += 2) matches.push([sides[i]!, sides[i + 1]!]);
  return matches;
}
